/**
 * 打字时把下注栏抬到键盘上方（牌桌嘴炮 · 边打字边下注）。
 *
 * 从下往上：键盘 → 输入框 → 下注栏 → 牌桌。做法：
 *  1. **冻住壳层与舞台**：聚焦那一刻量下 `.app-shell` 与 `.stage` 的高度写成 px。
 *     会把布局视口一起压缩的 WebView（微信 X5、部分安卓）上，舞台因此不变 ——
 *     不触发 ResizeObserver、不重算密度、座位不跳。
 *  2. **锁住视口监听**（`keyboardLock`）：键盘不是工具栏，也不是折叠屏合上。
 *  3. **算抬升量**（键盘动了、或下注栏自己变了形都要重算）：打字态的抽屉（`.talk` 翻到 dockbind 下方，
 *     只剩输入行）的底边要落在可视区底边上，`lift = dockbind 底边 + 抽屉高 − 可视区底边`，写进 `--kb-lift`，CSS 把 `.dock` 往上平移。
 *     iOS（布局视口不缩、可视区上移）与安卓（布局视口一起缩）两种情况下这个式子都成立：
 *     壳层高度已冻住，可视区底边一律取 `vv.offsetTop + vv.height`。
 *
 * 位移量直接写 DOM 变量，不进 React state（红线 #48 的同一类问题：键盘动画期间每帧都在变）。
 */
import { useEffect } from 'react'
import type { RefObject } from 'react'

import { setKeyboardLocked } from '../styles/keyboardLock.js'
import { noteTyping } from './typingGuard.js'

/** 收键盘后等可视区恢复的最长时间（键盘收起动画约 250ms） */
const SETTLE_MS = 400

/** 每次聚焦一个新版本号：撤冻结时只拆自己那一轮，不拆紧接着又聚焦的新一轮 */
let revSeq = 0

interface VisualViewportLike {
  readonly height: number
  readonly offsetTop: number
  addEventListener(type: string, cb: () => void): void
  removeEventListener(type: string, cb: () => void): void
}

/**
 * 元素底边相对祖先的布局位置（不含 transform —— 下注栏自己就在被平移）。
 * `offsetTop` 是相对父级**内边框**算的，父级的上边框（.dock 有 1px）要自己加上。
 */
function offsetBottomWithin(el: HTMLElement, ancestor: HTMLElement): number {
  let y = el.offsetHeight
  let node: HTMLElement | null = el
  while (node !== null && node !== ancestor) {
    const parent: HTMLElement | null = node.offsetParent instanceof HTMLElement ? node.offsetParent : null
    y += node.offsetTop + (parent !== null && parent !== ancestor ? parent.clientTop : 0)
    node = parent
  }
  return y
}

export function useKeyboardLift(
  active: boolean,
  anchorRef: RefObject<HTMLElement | null>,
  rowRef: RefObject<HTMLElement | null>,
): void {
  useEffect(() => {
    const anchor = anchorRef.current
    if (!active || anchor === null) return undefined
    const shell = anchor.closest('.app-shell')
    if (!(shell instanceof HTMLElement)) return undefined
    const stage = shell.querySelector('.stage')
    const vv = (globalThis as { visualViewport?: VisualViewportLike }).visualViewport ?? null

    shell.style.setProperty('--kb-shell-h', `${String(shell.clientHeight)}px`)
    if (stage instanceof HTMLElement) shell.style.setProperty('--kb-stage-h', `${String(stage.clientHeight)}px`)
    const myRev = String(++revSeq)
    shell.dataset['composing'] = 'on'
    shell.dataset['composingRev'] = myRev
    setKeyboardLocked(true)

    let raf = 0
    const measure = (): void => {
      raf = 0
      // iOS 聚焦时偶尔把整页往上滚，把固定定位的壳层带离原位；拉回来，可视区偏移才可信
      if ((globalThis.scrollY ?? 0) > 0) globalThis.scrollTo?.(0, 0)
      const visibleBottom = vv === null ? (globalThis.innerHeight ?? 0) : vv.offsetTop + vv.height
      const rowH = rowRef.current?.offsetHeight ?? 0
      const lift = Math.max(0, offsetBottomWithin(anchor, shell) + rowH - visibleBottom)
      shell.style.setProperty('--kb-lift', `${String(Math.round(lift))}px`)
    }
    const schedule = (): void => {
      if (raf !== 0) return
      raf = typeof requestAnimationFrame === 'function' ? requestAnimationFrame(measure) : (measure(), 0)
    }
    schedule()
    vv?.addEventListener('resize', schedule)
    vv?.addEventListener('scroll', schedule)
    // 打字期间下注栏会变形（轮到你：空闲条 → 行动栏；加注抽屉开合；输入行多出两张小牌），
    // 键盘没动、可视区也没变，只有尺寸监听看得见 —— 不重算的话输入行会被挤出屏幕底边
    const ro = typeof ResizeObserver === 'function' ? new ResizeObserver(schedule) : null
    ro?.observe(anchor)
    if (rowRef.current !== null) ro?.observe(rowRef.current)

    return () => {
      if (raf !== 0 && typeof cancelAnimationFrame === 'function') cancelAnimationFrame(raf)
      vv?.removeEventListener('resize', schedule)
      vv?.removeEventListener('scroll', schedule)
      ro?.disconnect()
      // 下注栏马上落回原位；这一下它在手指底下移动，给 0.3 秒护栏
      if (shell.dataset['composingRev'] === myRev) shell.style.setProperty('--kb-lift', '0px')
      noteTyping()

      // 壳层的冻结要等键盘真的收完再撤：安卓上键盘还在往下收时就放开，舞台会跟着键盘一帧帧长回来
      let done = false
      const release = (): void => {
        if (done) return
        done = true
        clearTimeout(timer)
        vv?.removeEventListener('resize', onVv)
        // 期间又聚焦了（收键盘的动画还没走完又点回输入框）：新的一轮已经接管，别拆它的台
        if (shell.dataset['composingRev'] !== myRev) return
        delete shell.dataset['composing']
        delete shell.dataset['composingRev']
        shell.style.removeProperty('--kb-shell-h')
        shell.style.removeProperty('--kb-stage-h')
        shell.style.removeProperty('--kb-lift')
        setKeyboardLocked(false)
      }
      const onVv = (): void => {
        const inner = globalThis.innerHeight ?? 0
        if (vv !== null && inner - (vv.offsetTop + vv.height) < 2) release()
      }
      const timer = setTimeout(release, SETTLE_MS)
      vv?.addEventListener('resize', onVv)
    }
  }, [active, anchorRef, rowRef])
}
